import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Gauge, Power, PowerOff, DollarSign, Coins, Activity, AlertTriangle, Save } from "lucide-react";

export default function Usage() {
  const qc = useQueryClient();
  const [budget, setBudget] = useState("");
  const { data } = useQuery({
    queryKey: ["usage"],
    queryFn: async () => (await api.get("/usage/summary")).data,
    refetchInterval: 10000,
  });

  const kill = useMutation({
    mutationFn: async (enabled) => (await api.post("/usage/kill", { enabled })).data,
    onSuccess: (s) => { toast[s.llm_enabled ? "success" : "warning"](s.llm_enabled ? "LLM spending ENABLED" : "LLM spending STOPPED (kill switch active)"); qc.invalidateQueries({ queryKey: ["usage"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const saveBudget = useMutation({
    mutationFn: async () => (await api.post("/usage/budget", { limit_usd: parseFloat(budget || "0") })).data,
    onSuccess: () => { toast.success("Monthly budget saved"); setBudget(""); qc.invalidateQueries({ queryKey: ["usage"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const poller = useMutation({
    mutationFn: async (enabled) => (await api.post("/ops/poller", { enabled })).data,
    onSuccess: (s) => { toast[s.poller_enabled ? "success" : "warning"](s.poller_enabled ? "Mailbox auto-processing ENABLED" : "Mailbox auto-processing PAUSED"); qc.invalidateQueries({ queryKey: ["usage"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const [allow, setAllow] = useState("");
  const saveAllow = useMutation({
    mutationFn: async () => (await api.post("/ops/allowlist", { domains: allow.split(/[,\n]/).map((s) => s.trim()).filter(Boolean) })).data,
    onSuccess: () => { toast.success("Sender allowlist saved"); qc.invalidateQueries({ queryKey: ["usage"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!data) return <div className="p-6 text-zinc-500">Loading…</div>;
  const on = data.llm_enabled;
  const usd = (n) => `$${(n ?? 0).toFixed(2)}`;
  const cap = data.budget_limit_usd || 0;
  const pct = data.budget_used_pct;

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3">
        <Gauge className="h-5 w-5 text-blue-400" />
        <h1 className="font-heading text-lg font-semibold tracking-tight">Usage & Budget</h1>
        <span className="text-xs font-mono text-zinc-500">LLM token spend · estimated</span>
      </header>

      <div className="p-6 space-y-5 max-w-5xl">
        {/* Kill switch */}
        <div data-testid="kill-switch-panel" className={`rounded-sm border p-4 flex items-center gap-4 ${on ? "border-emerald-700/50 bg-emerald-950/20" : "border-red-700/60 bg-red-950/30"}`}>
          {on ? <Power className="h-6 w-6 text-emerald-400" /> : <PowerOff className="h-6 w-6 text-red-400" />}
          <div className="flex-1">
            <p className="text-sm font-semibold text-zinc-100">Emergency kill switch</p>
            <p className="text-xs text-zinc-400">{on ? "LLM calls are ENABLED — Roket is spending." : "LLM calls are STOPPED — no spend. Classification falls back to TARIC candidates."}</p>
            {!on && data.killed_by && <p className="text-[10px] text-red-400 mt-0.5">Stopped by {data.killed_by} · {data.killed_at?.slice(0, 16).replace("T", " ")}</p>}
          </div>
          <button data-testid="kill-toggle-btn" onClick={() => { if (on ? window.confirm("Stop ALL LLM spending now?") : true) kill.mutate(!on); }}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${on ? "bg-red-600 hover:bg-red-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}>
            {on ? "STOP spending" : "Resume spending"}
          </button>
        </div>

        {data.budget_reached && (
          <div className="flex items-center gap-2 text-xs text-amber-400 border border-amber-700/50 bg-amber-950/20 rounded-sm px-3 py-2">
            <AlertTriangle className="h-4 w-4" /> Monthly budget reached — LLM auto-paused until budget is raised or the month rolls over.
          </div>
        )}

        {/* Mailbox auto-processing (poller) switch */}
        <div data-testid="poller-panel" className={`rounded-sm border p-4 flex items-center gap-4 ${data.poller_enabled ? "border-zinc-700 bg-zinc-950/40" : "border-amber-700/60 bg-amber-950/20"}`}>
          {data.poller_enabled ? <Power className="h-5 w-5 text-emerald-400" /> : <PowerOff className="h-5 w-5 text-amber-400" />}
          <div className="flex-1">
            <p className="text-sm font-semibold text-zinc-100">Mailbox auto-processing</p>
            <p className="text-xs text-zinc-400">{data.poller_enabled ? "Roket polls the shared mailbox and opens tickets on a bracket trigger e.g. [classify]." : "PAUSED — Roket ignores all incoming mail (no tickets, no replies)."}</p>
          </div>
          <button data-testid="poller-toggle-btn" onClick={() => poller.mutate(!data.poller_enabled)}
            className={`px-4 py-2 rounded-sm text-sm font-semibold ${data.poller_enabled ? "bg-amber-600 hover:bg-amber-500 text-white" : "bg-emerald-600 hover:bg-emerald-500 text-white"}`}>
            {data.poller_enabled ? "Pause mailbox" : "Resume mailbox"}
          </button>
        </div>

        {/* Sender allowlist */}
        <div data-testid="allowlist-panel" className="rounded-sm border border-zinc-800 p-4 space-y-2">
          <p className="text-sm font-medium text-zinc-200">Sender allowlist <span className="text-[10px] text-zinc-500">(optional — empty = allow any sender, rely on the bracket trigger)</span></p>
          <p className="text-[11px] text-zinc-500">Only mail from these addresses/domains will be processed. One per line or comma-separated. Current: <span className="font-mono text-zinc-300">{(data.sender_allowlist || []).join(", ") || "— none —"}</span></p>
          <textarea data-testid="allowlist-textarea" rows={3} value={allow} onChange={(e) => setAllow(e.target.value)}
            placeholder={"europacific.com\npartner.europacific.com"} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs font-mono text-zinc-200 focus:border-blue-500 outline-none" />
          <button data-testid="allowlist-save-btn" onClick={() => saveAllow.mutate()} className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-900"><Save className="h-3.5 w-3.5" /> Save allowlist</button>
        </div>

        {/* Spend cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card icon={DollarSign} label="Spend today" value={usd(data.spend_today?.cost_usd)} sub={`${data.spend_today?.calls || 0} calls · ${(data.spend_today?.tokens || 0).toLocaleString()} tok`} testid="card-today" />
          <Card icon={DollarSign} label="Spend this month" value={usd(data.spend_month?.cost_usd)} sub={`${data.spend_month?.calls || 0} calls · ${(data.spend_month?.tokens || 0).toLocaleString()} tok`} testid="card-month" />
          <Card icon={Coins} label="Spend all-time" value={usd(data.spend_all?.cost_usd)} sub={`${(data.spend_all?.tokens || 0).toLocaleString()} tokens`} testid="card-all" />
        </div>

        {data.blocked_month > 0 && (
          <div data-testid="blocked-note" className="flex items-center gap-2 text-xs text-zinc-400 border border-zinc-800 bg-zinc-950/40 rounded-sm px-3 py-2">
            <PowerOff className="h-4 w-4 text-red-400" /> <span><b className="text-zinc-200">{data.blocked_month}</b> LLM call(s) BLOCKED this month by the kill switch / budget — no spend incurred.</span>
          </div>
        )}

        {/* Budget */}
        <div className="rounded-sm border border-zinc-800 p-4 space-y-2" data-testid="budget-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-200">Monthly budget cap</p>
            <span className="text-xs font-mono text-zinc-400">{cap > 0 ? `${usd(data.spend_month?.cost_usd)} / ${usd(cap)}` : "no cap set"}</span>
          </div>
          {cap > 0 && (
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div className={`h-full ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <input data-testid="budget-input" type="number" min="0" step="1" value={budget} onChange={(e) => setBudget(e.target.value)}
              placeholder={cap > 0 ? String(cap) : "e.g. 50"} className="w-40 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm font-mono text-zinc-200 focus:border-blue-500 outline-none" />
            <span className="text-xs text-zinc-500">USD / month (0 = unlimited)</span>
            <button data-testid="budget-save-btn" onClick={() => saveBudget.mutate()} disabled={budget === ""}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-900 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save</button>
          </div>
          <p className="text-[10px] text-zinc-600">When the cap is hit, Roket automatically stops LLM spending until you raise it or the month resets.</p>
        </div>

        {/* By endpoint */}
        <div className="rounded-sm border border-zinc-800 overflow-hidden">
          <div className="px-3 py-2 bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> Spend by endpoint (this month)</div>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500"><th className="text-left py-1.5 px-3">Endpoint</th><th className="text-right py-1.5 px-3">Calls</th><th className="text-right py-1.5 px-3">Tokens</th><th className="text-right py-1.5 px-3">Cost</th></tr></thead>
            <tbody>
              {(data.by_tag || []).map((r) => (
                <tr key={r.tag} className="border-t border-zinc-800/60"><td className="py-1.5 px-3 text-zinc-200 font-mono text-xs">{r.tag}</td><td className="py-1.5 px-3 text-right text-zinc-400">{r.calls}</td><td className="py-1.5 px-3 text-right text-zinc-400">{r.tokens.toLocaleString()}</td><td className="py-1.5 px-3 text-right text-zinc-200">{usd(r.cost_usd)}</td></tr>
              ))}
              {(data.by_tag || []).length === 0 && <tr><td colSpan={4} className="py-8 text-center text-zinc-600 text-xs">No LLM usage yet this month.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-zinc-600">Costs are ESTIMATED (~4 chars/token) at ${data.pricing?.in_per_million_usd}/M in, ${data.pricing?.out_per_million_usd}/M out. For exact billing, top-up/track via Emergent Profile → Universal Key.</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-zinc-800 p-4 bg-zinc-950/40">
      <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-wide"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className="text-2xl font-semibold text-zinc-100 mt-1">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>
    </div>
  );
}
